/**
 * ClassNetworkStrip — Pillar #10 (5-year retention).
 *
 * "12 in CS101 are studying right now — Join a room"
 *
 * Mounts on SubjectOverviewPage. Returns null when nobody's active to
 * avoid an empty box (and to keep solo-class students from feeling alone
 * in a bad way). Backend: GET /api/class-network/{subject_id}/active.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Users, ArrowRight, Share2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

export default function ClassNetworkStrip({ subjectId, subjectName }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancel = false;
    if (!subjectId) return;
    (async () => {
      try {
        const res = await api.get(apiUrl(`/class-network/${subjectId}/active`));
        if (!cancel) setData(res.data);
      } catch { /* silent */ }
    })();
    const id = setInterval(async () => {
      try {
        const res = await api.get(apiUrl(`/class-network/${subjectId}/active`));
        if (!cancel) setData(res.data);
      } catch { /* silent */ }
    }, 60_000);
    return () => { cancel = true; clearInterval(id); };
  }, [subjectId]);

  const shareClass = async () => {
    const ogBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${ogBase}/api/og/class/${subjectId}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Class link copied — paste anywhere for a rich preview');
    } catch { toast.error('Copy failed'); }
  };

  if (!data || data.active_count === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}
      className="border border-[var(--isp-glass-border)] bg-[var(--isp-glass)] px-4 py-3 flex items-center justify-between gap-3"
      data-testid="class-network-strip"
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="flex -space-x-2 shrink-0">
          {data.sample.slice(0, 4).map((u, idx) => (
            u.picture ? (
              <img
                key={u.id || idx}
                src={u.picture}
                alt={u.name || ''}
                className="w-7 h-7 rounded-full border-2 border-[var(--isp-page)] object-cover"
              />
            ) : (
              <div
                key={u.id || idx}
                className="w-7 h-7 rounded-full border-2 border-[var(--isp-page)] bg-[#002FA7] text-white text-[10px] font-bold flex items-center justify-center"
                title={u.name || ''}
              >
                {(u.name || '?').charAt(0).toUpperCase()}
              </div>
            )
          ))}
          {data.active_count > 4 && (
            <div className="w-7 h-7 rounded-full border-2 border-[var(--isp-page)] bg-[var(--isp-glass-inner)] text-[var(--isp-heading)] text-[10px] font-bold flex items-center justify-center">
              +{data.active_count - 4}
            </div>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--isp-heading)] truncate" data-testid="class-network-count">
            <span className="text-[#002FA7]">{data.active_count}</span> in {subjectName || 'this class'} studying right now
            <span className="ml-2 inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse align-middle" />
          </p>
          <p className="text-xs text-[var(--isp-muted)]">Active in the last 15 minutes</p>
        </div>
      </div>
      <Link
        to="/study-rooms"
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#002FA7] hover:bg-[#00227A] text-white text-xs font-semibold transition-colors shrink-0"
        data-testid="class-network-join-btn"
      >
        Join a room
        <Users className="w-3.5 h-3.5" />
        <ArrowRight className="w-3 h-3" />
      </Link>
      <button
        onClick={shareClass}
        className="inline-flex items-center gap-1 px-3 py-2 border border-[var(--isp-glass-border)] hover:border-[#002FA7] text-[var(--isp-body)] hover:text-[#002FA7] text-xs font-medium transition-colors shrink-0"
        data-testid="class-network-share-btn"
        title="Copy a rich-preview link to share"
        aria-label="Share class network link"
      >
        <Share2 className="w-3.5 h-3.5" />
      </button>
    </motion.div>
  );
}
